import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { isAxiosError } from 'axios';
import DialogContentText from '@mui/material/DialogContentText';
import Button from '@mui/material/Button';
import Modal from './Modal';
import IdleTimerDebugPanel from './IdleTimerDebugPanel';
import { refreshSession } from '../api';
import { setSessionId } from '../security/authStorage';
import { performSignOut } from '../security/signOut';
import { createLogger } from '../utils/logging';

const logger = createLogger('IdleTimer');

// Time constants (in milliseconds)
const DEFAULT_IDLE_TIME_MS = 1200 * 1000; // 20 minutes
const MODAL_TIMEOUT_MS = 60 * 1000; // 1 minute before auto logout

// Sliding session refresh: a user who stays continuously active never goes idle, so the
// onIdle/"stay active" flow below never fires for them. Without a proactive heartbeat their
// server-side session would still expire out from under them.
const DEFAULT_HEARTBEAT_INTERVAL_MS = 300 * 1000; // 5 minutes, interim pending confirmed backend TTL

// If a heartbeat fails, retry once after a short delay rather than waiting for the next cycle.
const HEARTBEAT_RETRY_DELAY_MS = 60 * 1000; // 1 minute
const MAX_HEARTBEAT_RETRIES = 1;

// SessionStorage key allowing testers to temporarily override the idle timeout
// on a deployed lower environment without a redeploy. Non-prod only, like debug mode.
const IDLE_TIMER_TIMEOUT_OVERRIDE_KEY = 'IDLE_TIMER_TIMEOUT_OVERRIDE_SECONDS';

// SessionStorage key allowing testers to override the heartbeat interval the same way. Non-prod only.
const HEARTBEAT_INTERVAL_OVERRIDE_KEY = 'SESSION_HEARTBEAT_INTERVAL_OVERRIDE_SECONDS';

// Only allow non-default behavior (debug mode, timeout override) in non-production
// environments (prd = production)
const isNonProdEnv = (): boolean => {
  const apiEnv = process.env.REACT_APP_API_ENV || 'dev';
  return apiEnv !== 'prd';
};

// Helper to safely get sessionStorage value
const getSessionStorageBoolean = (key: string): boolean => {
  try {
    return sessionStorage.getItem(key) === 'true';
  } catch (error) {
    logger.warn('SessionStorage Access Failed', `Unable to read ${key}`, { error });
    return false;
  }
};

// Helper to safely set sessionStorage value
const setSessionStorageBoolean = (key: string, value: boolean): void => {
  try {
    if (value) {
      sessionStorage.setItem(key, 'true');
    } else {
      sessionStorage.removeItem(key);
    }
  } catch (error) {
    logger.warn('SessionStorage Access Failed', `Unable to write ${key}`, { error });
  }
};

// Helper to safely read a positive integer (seconds) from sessionStorage
const getSessionStorageSeconds = (key: string): number | null => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = parseInt(raw, 10);
    if (isNaN(parsed) || parsed <= 0) {
      logger.warn('Invalid SessionStorage Value', `Got "${raw}" for ${key}, ignoring override`);
      return null;
    }
    return parsed;
  } catch (error) {
    logger.warn('SessionStorage Access Failed', `Unable to read ${key}`, { error });
    return null;
  }
};

const IdleTimerContainer: React.FC = () => {
  const modalSessionTimeOutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [idleTimeRemaining, setIdleTimeRemaining] = useState<number>(0);
  const [modalTimeRemaining, setModalTimeRemaining] = useState<number>(0);
  const [debugMode, setDebugMode] = useState<boolean>(() => {
    if (!isNonProdEnv()) {
      return false;
    }
    return getSessionStorageBoolean('IDLE_TIMER_DEBUG');
  });

  // Get idle time from environment or use default (in milliseconds).
  // Memoized so the override/env lookup (and its log line) only runs once per mount,
  // instead of on every re-render (this component re-renders every 500ms via the
  // idle-time-remaining ticker below).
  const idleTimeMs = useMemo(() => {
    // Lower-env-only temp workaround: sessionStorage override lets testers change the
    // idle timeout on a deployed environment without a redeploy. Takes precedence
    // over the env var/default. Not honored in prd.
    if (isNonProdEnv()) {
      const overrideSeconds = getSessionStorageSeconds(IDLE_TIMER_TIMEOUT_OVERRIDE_KEY);
      if (overrideSeconds !== null) {
        logger.info('Idle Timeout Override', `Using sessionStorage override of ${overrideSeconds}s`, {
          overrideSeconds
        });
        return overrideSeconds * 1000;
      }
    }

    if (!process.env.REACT_APP_IDLE_TIME) return DEFAULT_IDLE_TIME_MS;
    const parsed = parseInt(process.env.REACT_APP_IDLE_TIME, 10);
    if (isNaN(parsed) || parsed <= 0) {
      logger.warn('Invalid REACT_APP_IDLE_TIME', `Got "${process.env.REACT_APP_IDLE_TIME}", using default`);
      return DEFAULT_IDLE_TIME_MS;
    }
    return parsed * 1000;
  }, []);

  const idleTimeDisplaySeconds = idleTimeMs / 1000;

  // Get heartbeat interval from environment override or use default (in milliseconds).
  // Memoized for the same reason as idleTimeMs above.
  const heartbeatIntervalMs = useMemo(() => {
    if (isNonProdEnv()) {
      const overrideSeconds = getSessionStorageSeconds(HEARTBEAT_INTERVAL_OVERRIDE_KEY);
      if (overrideSeconds !== null) {
        logger.info('Heartbeat Interval Override', `Using sessionStorage override of ${overrideSeconds}s`, {
          overrideSeconds
        });
        return overrideSeconds * 1000;
      }
    }
    return DEFAULT_HEARTBEAT_INTERVAL_MS;
  }, []);

  // Timestamp of the last successful refresh (heartbeat or "stay active" modal), for logging.
  const lastRefreshRef = useRef<number>(Date.now());
  // Timestamp of the last heartbeat attempt, success or failure. Gates the visibilitychange
  // catch-up below so a failing heartbeat isn't retried on every tab focus/blur.
  const lastHeartbeatAttemptRef = useRef<number>(Date.now());
  const heartbeatInFlightRef = useRef(false);
  const heartbeatRetryCountRef = useRef(0);
  const heartbeatRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Mirrors modalIsOpen for sendHeartbeat's retry path, which fires from a standalone
  // setTimeout outside the tick/visibilitychange callbacks that already gate on it.
  const modalIsOpenRef = useRef(false);

  const { getRemainingTime, pause, resume, reset } = useIdleTimer({
    timeout: idleTimeMs,
    onIdle: () => {
      logger.info('User Idle', 'User has been idle, showing modal', {
        idleTimeMs
      });
      pause();
      setModalIsOpen(true);
      setModalTimeRemaining(MODAL_TIMEOUT_MS / 1000);

      modalSessionTimeOutRef.current = setTimeout(() => {
        logger.info('Modal Timeout', 'User exceeded modal timeout, logging out');
        handleLogOut();
      }, MODAL_TIMEOUT_MS);
    },
    onActive: () => {
      logger.debug('User Active', 'User activity detected', {
        idleTimeRemaining: Math.ceil(getRemainingTime() / 1000)
      });
    },
    debounce: 500,
  });

  const handleStayActive = useCallback(async () => {
    setModalIsOpen(false);
    logger.info('User Stay Active', 'User chose to stay active', {
      action: 'refresh_session'
    });

    if (modalSessionTimeOutRef.current) {
      clearTimeout(modalSessionTimeOutRef.current);
      modalSessionTimeOutRef.current = null;
    }

    reset();
    resume();

    try {
      const data = await refreshSession();
      if (data?.sessionId) {
        setSessionId(data.sessionId);
        lastRefreshRef.current = Date.now();
        lastHeartbeatAttemptRef.current = Date.now();
        logger.debug('Session Refreshed', 'Session ID updated successfully');
      }
    } catch (error) {
      logger.error('Session Refresh Failed', 'Unable to refresh session, forcing sign-out', { error });
      await performSignOut();
    }
  }, [reset, resume]);

  // Keeps a continuously-active user's session alive on a fixed cadence, since they never
  // trigger the idle-timeout modal above. A 401 means the backend has confirmed the session
  // is unrecoverable (e.g. the IdP rejected the refresh grant after a long sleep/idle period,
  // like a laptop waking up) - sign out immediately rather than waiting on a future real
  // request to fail. Any other failure (network blip, unrelated error) retries once shortly
  // after, then falls back to waiting for the next cycle - a single transient blip shouldn't
  // kick out an active user.
  const sendHeartbeat = useCallback(async () => {
    if (heartbeatInFlightRef.current || modalIsOpenRef.current) return;
    lastHeartbeatAttemptRef.current = Date.now();
    heartbeatInFlightRef.current = true;
    try {
      const data = await refreshSession();
      if (data?.sessionId) {
        setSessionId(data.sessionId);
        lastRefreshRef.current = Date.now();
        logger.debug('Session Heartbeat', 'Sliding session refresh succeeded');
      }
      heartbeatRetryCountRef.current = 0;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 401) {
        heartbeatRetryCountRef.current = 0;
        logger.error('Session Heartbeat Failed', 'Session unrecoverable, forcing sign-out', { error });
        await performSignOut();
      } else if (heartbeatRetryCountRef.current < MAX_HEARTBEAT_RETRIES) {
        heartbeatRetryCountRef.current += 1;
        logger.warn('Session Heartbeat Failed', 'Unable to refresh session, retrying shortly', { error });
        heartbeatRetryTimeoutRef.current = setTimeout(() => {
          sendHeartbeat();
        }, HEARTBEAT_RETRY_DELAY_MS);
      } else {
        heartbeatRetryCountRef.current = 0;
        logger.warn('Session Heartbeat Failed', 'Unable to refresh session, will retry next cycle', { error });
      }
    } finally {
      heartbeatInFlightRef.current = false;
    }
  }, []);

  const handleLogOut = useCallback(async () => {
    setModalIsOpen(false);

    if (modalSessionTimeOutRef.current) {
      clearTimeout(modalSessionTimeOutRef.current);
      modalSessionTimeOutRef.current = null;
    }

    logger.info('User Logout', 'User logging out', {
      reason: 'idle_timeout'
    });

    await performSignOut();
  }, []);

  useEffect(() => {
    modalIsOpenRef.current = modalIsOpen;
  }, [modalIsOpen]);

  // Track idle time remaining
  useEffect(() => {
    const interval = setInterval(() => {
      setIdleTimeRemaining(Math.ceil(getRemainingTime() / 1000));
    }, 500);

    return () => clearInterval(interval);
  }, [getRemainingTime]);

  // Sliding-session heartbeat: fires on a fixed cadence while the tab is visible and the
  // idle-timeout modal isn't already handling a refresh. Also catches up immediately when
  // the tab regains visibility after being hidden/throttled past the interval.
  useEffect(() => {
    const tick = () => {
      if (modalIsOpen) return; // modal owns the refresh while it's up
      if (document.visibilityState !== 'visible') return; // don't slide a session no one is watching
      sendHeartbeat();
    };

    const intervalId = setInterval(tick, heartbeatIntervalMs);

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || modalIsOpen) return;
      // Gate on the last *attempt*, not the last success — otherwise a failing heartbeat
      // never advances lastRefreshRef and gets re-triggered on every focus/blur event.
      const elapsed = Date.now() - lastHeartbeatAttemptRef.current;
      if (elapsed >= heartbeatIntervalMs) {
        sendHeartbeat();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [modalIsOpen, sendHeartbeat, heartbeatIntervalMs]);

  // Track modal countdown
  useEffect(() => {
    if (!modalIsOpen) return;

    let interval: ReturnType<typeof setInterval> | null = null;

    interval = setInterval(() => {
      setModalTimeRemaining((prev) => {
        const next = prev - 1;
        if (next <= 0 && interval) {
          clearInterval(interval);
          interval = null;
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [modalIsOpen]);

  useEffect(() => {
    return () => {
      if (modalSessionTimeOutRef.current) {
        clearTimeout(modalSessionTimeOutRef.current);
        modalSessionTimeOutRef.current = null;
      }
      if (heartbeatRetryTimeoutRef.current) {
        clearTimeout(heartbeatRetryTimeoutRef.current);
        heartbeatRetryTimeoutRef.current = null;
      }
    };
  }, []);

  return (
    <>
      {debugMode && (
        <IdleTimerDebugPanel
          idleTimeRemaining={idleTimeRemaining}
          idleTimeDisplaySeconds={idleTimeDisplaySeconds}
          modalIsOpen={modalIsOpen}
          modalTimeRemaining={modalTimeRemaining}
          onClose={() => {
            setDebugMode(false);
            setSessionStorageBoolean('IDLE_TIMER_DEBUG', false);
          }}
        />
      )}

      <Modal
        isOpen={modalIsOpen}
        onClose={handleStayActive}
        title="You have been idle for a while!"
        ariaLabelledBy="idle-dialog-title"
        ariaDescribedBy="idle-dialog-description"
        maxWidth="xl"
        disableEscapeKeyDown
        disableBackdropClick
        actions={
          <>
            <Button onClick={handleStayActive} variant="contained" color="primary">
              Stay active
            </Button>
            <Button onClick={handleLogOut} variant="outlined" color="primary">
              Logout
            </Button>
          </>
        }
      >
        <DialogContentText id="idle-dialog-description">
          You will be logged out in <strong>{modalTimeRemaining}</strong> seconds.
        </DialogContentText>
      </Modal>
    </>
  );
};

export default IdleTimerContainer;
